import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getInvestors, createInvestor, getFunds } from '../api';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';
import {
  UserPlus, Search, Users, ChevronDown, X, ShieldCheck, Building2,
  ArrowUpRight,
} from 'lucide-react';

const statusMeta = {
  PENDING:                 { color: '#475569', bg: '#F8FAFC', border: '#E2E8F0', label: 'Pending' },
  KYC_IN_PROGRESS:         { color: '#1D4ED8', bg: '#F8FAFC', border: '#DBEAFE', label: 'KYC In Progress' },
  SUITABILITY_IN_PROGRESS: { color: '#475569', bg: '#F8FAFC', border: '#E2E8F0', label: 'Suitability' },
  DOCUMENTS_PENDING:       { color: '#92400E', bg: '#FFFBEB', border: '#FDE68A', label: 'Docs Pending' },
  ACTIVE:                  { color: '#166534', bg: '#F8FAFC', border: '#DCFCE7', label: 'Active' },
  SUSPENDED:               { color: '#991B1B', bg: '#FEF2F2', border: '#FECACA', label: 'Suspended' },
  REJECTED:                { color: '#991B1B', bg: '#FEF2F2', border: '#FECACA', label: 'Rejected' },
  GDPR_ERASED:             { color: '#64748B', bg: '#F8FAFC', border: '#E2E8F0', label: 'GDPR Erased' },
};

const STATUSES = [
  'ALL', 'PENDING', 'KYC_IN_PROGRESS', 'SUITABILITY_IN_PROGRESS',
  'DOCUMENTS_PENDING', 'ACTIVE', 'SUSPENDED', 'REJECTED',
];

const COUNTRIES = [
  { group: 'Benelux', options: [{ value: 'LU', label: 'Luxembourg' }, { value: 'BE', label: 'Belgium' }, { value: 'NL', label: 'Netherlands' }] },
  { group: 'Western Europe', options: [
    { value: 'FR', label: 'France' }, { value: 'DE', label: 'Germany' }, { value: 'AT', label: 'Austria' },
    { value: 'CH', label: 'Switzerland' }, { value: 'GB', label: 'United Kingdom' }, { value: 'IE', label: 'Ireland' },
    { value: 'IT', label: 'Italy' }, { value: 'ES', label: 'Spain' }, { value: 'PT', label: 'Portugal' },
    { value: 'LI', label: 'Liechtenstein' }, { value: 'MC', label: 'Monaco' },
  ]},
  { group: 'Nordic', options: [{ value: 'SE', label: 'Sweden' }, { value: 'DK', label: 'Denmark' }, { value: 'FI', label: 'Finland' }, { value: 'NO', label: 'Norway' }, { value: 'IS', label: 'Iceland' }] },
  { group: 'Eastern Europe', options: [
    { value: 'PL', label: 'Poland' }, { value: 'CZ', label: 'Czech Republic' }, { value: 'SK', label: 'Slovakia' },
    { value: 'HU', label: 'Hungary' }, { value: 'RO', label: 'Romania' }, { value: 'BG', label: 'Bulgaria' },
    { value: 'HR', label: 'Croatia' }, { value: 'EE', label: 'Estonia' }, { value: 'LV', label: 'Latvia' },
    { value: 'LT', label: 'Lithuania' }, { value: 'GR', label: 'Greece' }, { value: 'CY', label: 'Cyprus' },
  ]},
  { group: 'Americas', options: [{ value: 'US', label: 'United States' }, { value: 'CA', label: 'Canada' }, { value: 'BR', label: 'Brazil' }] },
  { group: 'Asia & Pacific', options: [{ value: 'JP', label: 'Japan' }, { value: 'CN', label: 'China' }, { value: 'SG', label: 'Singapore' }, { value: 'HK', label: 'Hong Kong' }, { value: 'AU', label: 'Australia' }] },
  { group: 'Middle East', options: [{ value: 'AE', label: 'UAE' }, { value: 'SA', label: 'Saudi Arabia' }, { value: 'QA', label: 'Qatar' }] },
  { group: 'Other', options: [{ value: 'OTHER', label: 'Other' }] },
];

const ALL_OPTIONS = COUNTRIES.flatMap(g => g.options);
const isRestricted = (val) => val === '*** RESTRICTED ***';

const StatusPill = ({ status }) => {
  const meta = statusMeta[status] || statusMeta.PENDING;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      background: meta.bg,
      color: meta.color,
      border: `1px solid ${meta.border}`,
      padding: '3px 8px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {meta.label}
    </span>
  );
};

const RestrictedBadge = () => (
  <span style={{
    fontSize: 11,
    color: '#64748B',
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    padding: '3px 8px',
    borderRadius: 999,
    fontWeight: 600,
  }}>
    Restricted
  </span>
);

const NationalityDropdown = ({ value, onChange, theme }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = ALL_OPTIONS.find(o => o.value === value);

  const filtered = search.trim()
    ? COUNTRIES.map(g => ({
        ...g,
        options: g.options.filter(o =>
          o.label.toLowerCase().includes(search.toLowerCase()) ||
          o.value.toLowerCase().includes(search.toLowerCase())
        )
      })).filter(g => g.options.length > 0)
    : COUNTRIES;

  return (
    <div style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(v => !v)} style={{
        width: '100%',
        padding: '10px 12px',
        borderRadius: 8,
        border: `1px solid ${open ? '#2563EB' : theme.border}`,
        fontSize: 13,
        color: selected ? theme.textPrimary : theme.textTertiary,
        background: theme.bgInput,
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxSizing: 'border-box',
      }}>
        <span>{selected ? selected.label : 'Select nationality'}</span>
        <ChevronDown size={14} color={theme.textTertiary} />
      </button>

      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 499 }} onClick={() => { setOpen(false); setSearch(''); }} />
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 500,
            background: theme.bgCard,
            borderRadius: 10,
            marginTop: 6,
            border: `1px solid ${theme.border}`,
            boxShadow: theme.shadowLg,
            overflow: 'hidden',
          }}>
            <div style={{ padding: 10, borderBottom: `1px solid ${theme.border}`, background: theme.bgBase }}>
              <div style={{ position: 'relative' }}>
                <Search size={13} color={theme.textTertiary} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)' }} />
                <input autoFocus type="text" placeholder="Search country" value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 10px 8px 30px',
                    borderRadius: 8,
                    border: `1px solid ${theme.border}`,
                    fontSize: 12,
                    outline: 'none',
                    boxSizing: 'border-box',
                    color: theme.textPrimary,
                    background: theme.bgInput,
                  }}
                />
              </div>
            </div>

            <div style={{ maxHeight: 230, overflowY: 'auto' }}>
              {filtered.map(group => (
                <div key={group.group}>
                  <div style={{
                    padding: '7px 12px 4px',
                    fontSize: 10,
                    fontWeight: 600,
                    color: theme.textTertiary,
                    textTransform: 'uppercase',
                    background: theme.bgBase,
                    borderBottom: `1px solid ${theme.border}`,
                  }}>
                    {group.group}
                  </div>
                  {group.options.map(opt => (
                    <button key={opt.value} type="button" onClick={() => { onChange(opt.value); setOpen(false); setSearch(''); }}
                      style={{
                        width: '100%',
                        padding: '9px 14px',
                        fontSize: 13,
                        cursor: 'pointer',
                        color: opt.value === value ? '#1D4ED8' : theme.textPrimary,
                        fontWeight: opt.value === value ? 600 : 400,
                        background: opt.value === value ? '#F8FAFC' : theme.bgCard,
                        border: 'none',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}>
                      <span>{opt.label}</span>
                      <span style={{ fontSize: 10, color: theme.textTertiary, fontFamily: 'monospace' }}>{opt.value}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Investors = () => {
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { theme } = useTheme();

  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [funds, setFunds] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    fund_id: '',
    investor_type: 'RETAIL',
    nationality: '',
  });

  useEffect(() => {
    Promise.all([getInvestors(), getFunds()])
      .then(([invRes, fundRes]) => {
        setInvestors(invRes.data || []);
        setFunds(fundRes.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = investors.filter(i => {
    if (filter !== 'ALL' && i.onboarding_status !== filter) return false;
    const q = search.toLowerCase();
    if (q && !i.pseudo_id?.toLowerCase().includes(q) && !i.full_name?.toLowerCase().includes(q)) return false;
    return true;
  });

  const activeCount = investors.filter(i => i.onboarding_status === 'ACTIVE').length;
  const pendingCount = investors.filter(i =>
    ['PENDING', 'KYC_IN_PROGRESS', 'SUITABILITY_IN_PROGRESS', 'DOCUMENTS_PENDING'].includes(i.onboarding_status)
  ).length;
  const exceptionCount = investors.filter(i => ['REJECTED', 'SUSPENDED'].includes(i.onboarding_status)).length;

  const resetForm = () => setForm({ full_name: '', email: '', fund_id: '', investor_type: 'RETAIL', nationality: '' });

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: `1px solid ${theme.border}`,
    fontSize: 13,
    color: theme.textPrimary,
    background: theme.bgInput,
    boxSizing: 'border-box',
    outline: 'none',
  };

  const primaryBtn = {
    background: '#2563EB',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    padding: '9px 15px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
  };

  const secondaryBtn = {
    background: theme.bgInput,
    color: theme.textSecondary,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    padding: '9px 15px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  };

  const investorName = (investor) => {
    if (isRestricted(investor.full_name)) return <RestrictedBadge />;
    return investor.full_name || <span style={{ color: theme.textTertiary }}>Erased</span>;
  };

  const initials = (investor) => {
    if (!investor.full_name || isRestricted(investor.full_name)) return '?';
    return investor.full_name.charAt(0).toUpperCase();
  };

  const handleCreate = async () => {
    if (!form.full_name || !form.email || !form.fund_id || !form.nationality) {
      setCreateError('Please fill in all required fields.');
      return;
    }

    setCreating(true);
    setCreateError(null);
    try {
      await createInvestor(form);
      const res = await getInvestors();
      setInvestors(res.data || []);
      setShowModal(false);
      resetForm();
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create investor.');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: theme.textTertiary, fontSize: 14 }}>Loading investors...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        marginBottom: 24,
        flexWrap: 'wrap',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.textPrimary }}>
            Investors
          </h1>
          <p style={{ margin: '5px 0 0', fontSize: 13, color: theme.textSecondary }}>
            Investor onboarding, status monitoring, and compliance access.
          </p>
        </div>
        <button onClick={() => { setShowModal(true); setCreateError(null); }} style={primaryBtn}>
          <UserPlus size={15} /> Add Investor
        </button>
      </div>

      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowSm,
        marginBottom: 16,
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          borderBottom: `1px solid ${theme.border}`,
        }}>
          {[
            ['Total', investors.length],
            ['Active', activeCount],
            ['In review', pendingCount],
            ['Exceptions', exceptionCount],
          ].map(([label, value], idx) => (
            <div key={label} style={{
              padding: '16px 18px',
              borderRight: !isMobile && idx < 3 ? `1px solid ${theme.border}` : 'none',
            }}>
              <p style={{ margin: 0, fontSize: 12, color: theme.textTertiary, fontWeight: 600 }}>{label}</p>
              <p style={{
                margin: '6px 0 0',
                fontSize: 24,
                fontWeight: 700,
                color: label === 'Exceptions' && value > 0 ? '#991B1B' : theme.textPrimary,
                lineHeight: 1,
              }}>
                {value}
              </p>
            </div>
          ))}
        </div>

        <div style={{
          padding: '14px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          <div style={{ position: 'relative', width: isMobile ? '100%' : 320 }}>
            <Search size={14} color={theme.textTertiary} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Search name or pseudo ID"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, paddingLeft: 34 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', maxWidth: '100%' }}>
            {STATUSES.map(s => {
              const active = filter === s;
              return (
                <button key={s} onClick={() => setFilter(s)} style={{
                  padding: '7px 10px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: `1px solid ${active ? '#CBD5E1' : theme.border}`,
                  background: active ? '#F8FAFC' : theme.bgInput,
                  color: active ? theme.textPrimary : theme.textSecondary,
                  whiteSpace: 'nowrap',
                }}>
                  {s === 'ALL' ? 'All' : statusMeta[s]?.label || s.replace(/_/g, ' ')}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowSm,
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 16px',
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
        }}>
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: theme.textPrimary }}>
              Investor Register
            </p>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textTertiary }}>
              Showing {filtered.length} of {investors.length}
            </p>
          </div>
          <Users size={17} color={theme.textTertiary} />
        </div>

        {filtered.length === 0 ? (
          <div style={{ padding: '56px 24px', textAlign: 'center' }}>
            <Users size={34} color={theme.textTertiary} style={{ marginBottom: 10 }} />
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: theme.textPrimary }}>No investors found</p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>Try a different search or status filter.</p>
          </div>
        ) : isMobile ? (
          <div style={{ display: 'grid', gap: 8, padding: 10 }}>
            {filtered.map(investor => (
              <button key={investor.id} onClick={() => navigate(`/investors/${investor.id}`)} style={{
                textAlign: 'left',
                background: theme.bgInput,
                border: `1px solid ${theme.border}`,
                borderRadius: 10,
                padding: 13,
                cursor: 'pointer',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', gap: 10, minWidth: 0 }}>
                    <div style={{
                      width: 34,
                      height: 34,
                      borderRadius: 9,
                      background: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      color: '#334155',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 13,
                      fontWeight: 600,
                      flexShrink: 0,
                    }}>
                      {initials(investor)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>{investorName(investor)}</p>
                      <p style={{ margin: '3px 0 0', fontSize: 11, color: theme.textSecondary }}>{investor.investor_type || '-'}</p>
                    </div>
                  </div>
                  <StatusPill status={investor.onboarding_status} />
                </div>

                <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <code style={{ color: theme.textSecondary, fontSize: 11, fontWeight: 600 }}>
                    {investor.pseudo_id}
                  </code>
                  <span style={{ fontSize: 11, color: theme.textTertiary }}>
                    {new Date(investor.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: theme.bgBase, borderBottom: `1px solid ${theme.border}` }}>
                {['Investor', 'Pseudo ID', 'Type', 'Status', 'Created', ''].map(h => (
                  <th key={h} style={{
                    padding: '11px 16px',
                    textAlign: 'left',
                    fontSize: 10,
                    fontWeight: 600,
                    color: theme.textTertiary,
                    textTransform: 'uppercase',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((investor, idx) => (
                <tr key={investor.id} onClick={() => navigate(`/investors/${investor.id}`)}
                  style={{
                    borderBottom: idx < filtered.length - 1 ? `1px solid ${theme.border}` : 'none',
                    cursor: 'pointer',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = theme.bgCardHover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        color: '#334155',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 600,
                        flexShrink: 0,
                      }}>
                        {initials(investor)}
                      </div>
                      <div>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>{investorName(investor)}</p>
                        <p style={{ margin: '2px 0 0', fontSize: 11, color: theme.textTertiary }}>Investor profile</p>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <code style={{ color: theme.textSecondary, fontSize: 11, fontWeight: 600 }}>
                      {investor.pseudo_id}
                    </code>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: 12, color: theme.textSecondary }}>{investor.investor_type || '-'}</td>
                  <td style={{ padding: '14px 16px' }}><StatusPill status={investor.onboarding_status} /></td>
                  <td style={{ padding: '14px 16px', fontSize: 12, color: theme.textSecondary }}>
                    {new Date(investor.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                    <ArrowUpRight size={15} color={theme.textTertiary} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: theme.bgOverlay,
          display: 'flex',
          alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: theme.bgCard,
            borderRadius: isMobile ? '18px 18px 0 0' : 12,
            width: '100%',
            maxWidth: isMobile ? '100%' : 520,
            maxHeight: isMobile ? '92vh' : '90vh',
            overflowY: 'auto',
            boxShadow: theme.shadowLg,
            border: isMobile ? 'none' : `1px solid ${theme.border}`,
          }}>
            <div style={{
              padding: 20,
              borderBottom: `1px solid ${theme.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'sticky',
              top: 0,
              background: theme.bgCard,
              zIndex: 1,
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: theme.textPrimary }}>
                  Add investor
                </h2>
                <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textSecondary }}>
                  Create the initial profile for onboarding.
                </p>
              </div>
              <button onClick={() => { setShowModal(false); resetForm(); }} style={{
                background: theme.bgInput,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                cursor: 'pointer',
                padding: 8,
                display: 'flex',
                color: theme.textSecondary,
              }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: 20 }}>
              <div style={{
                background: theme.bgInput,
                border: `1px solid ${theme.border}`,
                borderRadius: 10,
                padding: 12,
                marginBottom: 16,
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
              }}>
                <ShieldCheck size={16} color={theme.textSecondary} style={{ marginTop: 1, flexShrink: 0 }} />
                <p style={{ margin: 0, fontSize: 12, color: theme.textSecondary, lineHeight: 1.5 }}>
                  KYC, suitability, and documents are completed through the onboarding flow.
                </p>
              </div>

              {[
                { label: 'Full name', key: 'full_name', type: 'text', placeholder: 'John Smith' },
                { label: 'Email address', key: 'email', type: 'email', placeholder: 'john@example.com' },
              ].map(f => (
                <div key={f.key} style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: theme.textSecondary, marginBottom: 6 }}>
                    {f.label} <span style={{ color: '#991B1B' }}>*</span>
                  </label>
                  <input type={f.type} value={form[f.key]} placeholder={f.placeholder}
                    onChange={e => setForm(d => ({ ...d, [f.key]: e.target.value }))}
                    style={inputStyle}
                  />
                </div>
              ))}

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: theme.textSecondary, marginBottom: 6 }}>
                  Nationality <span style={{ color: '#991B1B' }}>*</span>
                </label>
                <NationalityDropdown value={form.nationality} onChange={val => setForm(d => ({ ...d, nationality: val }))} theme={theme} />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: theme.textSecondary, marginBottom: 6 }}>
                  Fund <span style={{ color: '#991B1B' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <Building2 size={14} color={theme.textTertiary} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
                  <select value={form.fund_id} onChange={e => setForm(d => ({ ...d, fund_id: e.target.value }))} style={{ ...inputStyle, paddingLeft: 34, cursor: 'pointer' }}>
                    <option value="">Select a fund</option>
                    {funds.map(f => <option key={f.id} value={f.id}>{f.fund_name}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: theme.textSecondary, marginBottom: 6 }}>
                  Investor type
                </label>
                <select value={form.investor_type} onChange={e => setForm(d => ({ ...d, investor_type: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="RETAIL">Retail Investor</option>
                  <option value="PROFESSIONAL">Professional Investor</option>
                  <option value="ELECTIVE_PROFESSIONAL">Elective Professional</option>
                  <option value="WELL_INFORMED">Well-Informed Investor</option>
                </select>
              </div>

              {createError && (
                <div style={{
                  background: '#FEF2F2',
                  borderRadius: 8,
                  padding: '10px 13px',
                  marginBottom: 14,
                  fontSize: 12,
                  color: '#991B1B',
                  fontWeight: 600,
                  border: '1px solid #FECACA',
                }}>
                  {createError}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => { setShowModal(false); resetForm(); }} style={secondaryBtn}>
                  Cancel
                </button>
                <button disabled={creating} onClick={handleCreate} style={{
                  ...primaryBtn,
                  background: creating ? '#93C5FD' : '#2563EB',
                  cursor: creating ? 'not-allowed' : 'pointer',
                }}>
                  {creating ? 'Creating...' : <><UserPlus size={14} /> Create Investor</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Investors;
